import { useQuery } from '@apollo/client/react';
import { motion } from 'framer-motion';
import { kebabCase } from 'lodash-es';
import { useTranslation } from 'next-i18next/pages';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useMemo } from 'react';

import { JobPositionDocument } from '@pflegia/data-access/multiposting.generated';
import { RegisterOrigin } from '@pflegia/types/api/candidate/register-origin';
import { FunnelQuestion } from '@pflegia/types/api/funnel/questions';
import { SourceDomain } from '@pflegia/types/api/source-domain/source-domain';
import FunnelErrorBoundary from '@pflegia/ui/funnel/components/base/FunnelErrorBoundary';
import { useFunnel } from '@pflegia/ui/funnel/hooks/use-funnel';
import { useFlavorFromRoute } from '@pflegia/ui/hooks/use-flavor-from-route';
import { ReactComponent as Close } from '@pflegia/ui/icons/times.svg';
import { FunnelAnswers } from '@pflegia/ui/pflegia/funnel/types/funnel-answers';
import { ExternalHeader } from '@pflegia/ui/shared/external-header/ExternalHeader';
import { GET_JOB_TITLES, GetJobTitlesData } from '@pflegia/ui/shared/graphql/job-title';
import MultipostingButtons from '@pflegia/ui/shared/multiposting-buttons/MultipostingButtons';
import Spinner from '@pflegia/ui/shared/spinner';
import { getPublicImageUrl } from '@pflegia/util/functions/s3/get-public-image-url';
import { isDefined } from '@pflegia/util/functions/type-guards';
import { getAppNameFromDomain } from '@pflegia/util/pflegia/get-app-name-from-domain';

import { useTrackAnswer } from '@domains/funnel/hooks/use-track-answer';
import { useTrackRegistrationStarted } from '@domains/funnel/hooks/use-track-registration-started';

import { multipostingQuestions } from '@funnel/data/multiposting-funnel-flow';

import { EXTERNAL_JOB, JOB_BOARD, JOB_DETAILS, JOBS } from '@shared/application/constants';
import useRegisterMethod from '@shared/hooks/use-register-method';

import LocalFunnelDecider from '../local-funnel/LocalFunnelDecider';

const MultipostingFunnel: React.FC = () => {
  useTrackRegistrationStarted();
  const { t } = useTranslation('funnel');
  const router = useRouter();
  const jobId = router.query.jobId as string;
  const registerOrigin = router.query.registerOrigin as RegisterOrigin;
  const isExternal = router.query.external === 'true';
  const { isTransparentFunnel } = useRegisterMethod();
  const isRegisterOriginPremiumLandingPage = registerOrigin === RegisterOrigin.PremiumLandingPage;
  const { sourceDomain } = useFlavorFromRoute();

  const { data } = useQuery(JobPositionDocument, { variables: { id: jobId, external: isExternal } });
  const [nameOfCity, jobSlug] = [data?.jobPosition.geoLocation?.city, data?.jobPosition.slug || data?.jobPosition.title].map(field =>
    field ? kebabCase(field) : 'undefined',
  );

  const similarJobsLink = useMemo(
    () => (isTransparentFunnel || isRegisterOriginPremiumLandingPage ? JOBS : JOB_BOARD),
    [isRegisterOriginPremiumLandingPage, isTransparentFunnel],
  );
  const jobDetailsLink = useMemo(
    () => (isTransparentFunnel ? `${JOB_DETAILS}/${nameOfCity}/${jobSlug}/${jobId}` : { pathname: EXTERNAL_JOB, query: { slug: jobId } }),
    [isTransparentFunnel, jobId, jobSlug, nameOfCity],
  );

  const questionKeys = multipostingQuestions.map(question => question.key);
  const { data: jobTitlesData } = useQuery<GetJobTitlesData>(GET_JOB_TITLES, { variables: { sortField: 'order', sortOrder: 'ASC' } });
  const track = useTrackAnswer();
  const { onAnswer, question, setValue, value, setDisableContinue } = useFunnel<
    GetJobTitlesData['allJobTitles'][0],
    FunnelAnswers,
    FunnelQuestion,
    { sourceDomain: SourceDomain }
  >({
    jobTitles: jobTitlesData?.allJobTitles ?? [],
    // @ts-expect-error Generics can clash
    onAnswer: track,
    // @ts-expect-error Generics can clash
    questions: multipostingQuestions,
    // @ts-expect-error Generics can clash
    jobPosition: data?.jobPosition,
    props: { sourceDomain },
  });

  return (
    <div className='flex h-auto min-h-screen flex-col' data-testid='multiposting-funnel'>
      {data && (
        <ExternalHeader
          links={[{ children: t('externalHeader.seeSimilarJobs'), href: similarJobsLink, key: similarJobsLink }]}
          logoSrc={getPublicImageUrl(data.jobPosition.employer?.logoKey)}
          name={data.jobPosition.employer?.name || ''}
          appName={getAppNameFromDomain(data.jobPosition.employer?.sourceDomains[0] as SourceDomain)}
        />
      )}
      <div className='relative mx-3 grid h-auto flex-grow auto-rows-min flex-col gap-8 pt-8 md:mx-8 md:justify-center md:pt-24 md:pb-3'>
        <Link className='text-gray-text hover:text-default absolute top-0 right-0 mt-2 p-3 transition-colors lg:m-12' href={jobDetailsLink}>
          <Close />
        </Link>
        {question ? (
          <motion.main
            animate={{ opacity: 1 }}
            className='mt-8 flex max-w-3xl flex-col gap-8'
            initial={{ opacity: 0 }}
            key={question}
            transition={{ duration: 0.25, ease: 'easeIn' }}>
            <FunnelErrorBoundary>
              <LocalFunnelDecider
                onAnswer={onAnswer}
                question={question}
                questionKeys={questionKeys}
                setValue={setValue}
                value={value}
                setDisableContinue={setDisableContinue}
              />
              {![
                // These components have their own buttons
                FunnelQuestion.CvFiles,
                FunnelQuestion.ExternalRegistration,
                FunnelQuestion.PersonalData,
                FunnelQuestion.PhoneNumber,
              ].includes(question) && (
                <MultipostingButtons
                  onBack={
                    question === multipostingQuestions[0].key
                      ? undefined
                      : () => {
                          router.back();
                        }
                  }
                  onNext={
                    isDefined(value)
                      ? () => {
                          onAnswer();
                        }
                      : undefined
                  }
                />
              )}
            </FunnelErrorBoundary>
          </motion.main>
        ) : (
          <Spinner />
        )}
      </div>
    </div>
  );
};

export default MultipostingFunnel;
